import { useReducer } from 'react';

const cleanupScoring = () => ({
  itemLoaded: false,
  itemReference: '',
  itemScoringReference: '',
  error: false,
  message: '',
  scoreState: [],
});

const setItemReference = (state, { itemReference }) => ({
  ...state,
  itemReference,
  itemScoringReference: `${itemReference}_rubric`,
});

const addScoringSection = (state) => ({
  ...state,
  scoreState: [
    ...state.scoreState,
    {
      sectionTitle: '',
      scoringInstructions: {
        content: [{ content: [], type: 'paragraph' }],
        type: 'doc',
      },
      scoringFeedback: [
        {
          feedback: {
            content: [{ content: [], type: 'paragraph' }],
            type: 'doc',
          },
          additional_feedback: {
            content: [{ content: [], type: 'paragraph' }],
            type: 'doc',
          },
          criterias: [],
        },
        {
          feedback: {
            content: [{ content: [], type: 'paragraph' }],
            type: 'doc',
          },
          additional_feedback: {
            content: [{ content: [], type: 'paragraph' }],
            type: 'doc',
          },
          criterias: [],
        },
      ],
    },
  ],
});

const addEmptyScoringSection = (state) => ({
  ...state,
  scoreState: [
    ...state.scoreState,
    {
      sectionTitle: '',
      scoringInstructions: '',
      scoringFeedback: [],
    },
  ],
});

const removeScoringSection = (state, { index }) => {
  if (state.scoreState.length === 1) return state;

  const newState = { ...state };
  newState.scoreState.splice(index, 1);

  return newState;
};

const moveScoringSectionUp = (state, { index }) => {
  if (index === 0) return state;

  const newState = { ...state };

  const previousItem = newState.scoreState[index - 1];
  newState.scoreState[index - 1] = newState.scoreState[index];
  newState.scoreState[index] = previousItem;

  return newState;
};

const moveScoringSectionDown = (state, { index }) => {
  if (index === state.scoreState.length - 1) return state;

  const newState = { ...state };

  const nextItem = newState.scoreState[index + 1];
  newState.scoreState[index + 1] = newState.scoreState[index];
  newState.scoreState[index] = nextItem;

  return newState;
};

const updateScoringSectionTitle = (state, { sectionTitle, index }) => {
  const newState = { ...state };

  newState.scoreState[index].sectionTitle = sectionTitle;

  return newState;
};

const updateScoringSectionInstructions = (
  state,
  { scoringInstructions, index },
) => {
  const newState = { ...state };

  newState.scoreState[index].scoringInstructions = scoringInstructions;

  return newState;
};

const addPoint = (state, { sectionIndex }) => {
  const newState = { ...state };

  newState.scoreState[sectionIndex].scoringFeedback.push({
    feedback: { type: 'doc', content: [{ type: 'paragraph' }] },
    additional_feedback: { type: 'doc', content: [{ type: 'paragraph' }] },
    criterias: [],
  });

  return newState;
};

const removePoint = (state, { sectionIndex, index }) => {
  const newState = { ...state };

  newState.scoreState[sectionIndex].scoringFeedback.splice(index, 1);

  return newState;
};

const updatePointScoringFeedback = (
  state,
  { feedback, sectionIndex, index },
) => {
  const newState = { ...state };

  newState.scoreState[sectionIndex].scoringFeedback[index].feedback = feedback;

  return newState;
};

const updatePointScoringAdditionalFeedback = (
  state,
  { additional_feedback, sectionIndex, index },
) => {
  const newState = { ...state };

  newState.scoreState[sectionIndex].scoringFeedback[index].additional_feedback =
    additional_feedback;

  return newState;
};

const addCriteria = (state, { sectionIndex, pointIndex }) => {
  const newState = { ...state };

  newState.scoreState[sectionIndex].scoringFeedback[pointIndex].criterias.push({
    description: { content: [{ content: [], type: 'paragraph' }], type: 'doc' },
  });

  return newState;
};

const updateCriteria = (
  state,
  { description, sectionIndex, pointIndex, index },
) => {
  const newState = { ...state };

  newState.scoreState[sectionIndex].scoringFeedback[pointIndex].criterias[
    index
  ].description = description;

  return newState;
};

const removeCriteria = (state, { sectionIndex, pointIndex, index }) => {
  const newState = { ...state };

  newState.scoreState[sectionIndex].scoringFeedback[
    pointIndex
  ].criterias.splice(index, 1);

  return newState;
};

const handlers = {
  cleanupScoring,
  setItemReference,
  addScoringSection,
  addEmptyScoringSection,
  removeScoringSection,
  moveScoringSectionUp,
  moveScoringSectionDown,
  updateScoringSectionTitle,
  updateScoringSectionInstructions,
  addPoint,
  removePoint,
  updatePointScoringFeedback,
  updatePointScoringAdditionalFeedback,
  addCriteria,
  updateCriteria,
  removeCriteria,
};

const scoringReducer = (state, action) => {
  if (Object.prototype.hasOwnProperty.call(handlers, action.type)) {
    return handlers[action.type](state, action.payload);
  }

  return state;
};

export default function useScoringReducer() {
  const initialState = {
    itemLoaded: false,
    itemReference: '',
    itemScoringReference: '',
    error: false,
    message: '',
    scoreState: [
      {
        sectionTitle: '',
        scoringInstructions: {
          content: [{ content: [], type: 'paragraph' }],
          type: 'doc',
        },
        scoringFeedback: [
          {
            feedback: {
              content: [{ content: [], type: 'paragraph' }],
              type: 'doc',
            },
            additional_feedback: {
              content: [{ content: [], type: 'paragraph' }],
              type: 'doc',
            },
            criterias: [],
          },
          {
            feedback: {
              content: [{ content: [], type: 'paragraph' }],
              type: 'doc',
            },
            additional_feedback: {
              content: [{ content: [], type: 'paragraph' }],
              type: 'doc',
            },
            criterias: [],
          },
        ],
      },
    ],
  };

  const [scoringSections, dispatch] = useReducer(scoringReducer, initialState);

  return [scoringSections, dispatch];
}
